import { Loader } from "@brexhq/component-library";
import styled from "@emotion/styled";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { margin } from "polished";
import React, { useCallback } from "react";

import {
  getForm,
  pushLeads,
  type LeadRequestBody,
} from "@brexhq/next/src/api/marketo";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import useLocation from "@brexhq/next/src/hooks/useLocation";
import { MarketingFormSubmittedEvent } from "@brexhq/next/src/telemetry/analyticsEvents";
import {
  logGetFormError,
  logPushLeadsError,
  logPushLeadsFailed,
} from "@brexhq/next/src/telemetry/logging";
import { spacingValuesDecoder } from "@brexhq/shared/types/SpacingScale";

import useSiteContext from "../../../hooks/useSiteContext";
import checkIfQualifiedEnabled from "../../../utils/qualified";
import { submitLeadRoutingRequest } from "../../modules/FormMultistep/dynamicLeadFormController";
import { bypassSuccess } from "../../modules/FormMultistep/utils";

import { Form } from "./components/Form";
import { ErrorMessage, SuccessMessage } from "./components/SubmitMessage";
import { usePrefillFromLead, useSubmit } from "./hooks";
import {
  MarketoFormProps,
  FormStatus,
  FormValues,
  FieldState,
  genericErrorMessage,
  DataType,
} from "./types";
import {
  validateField,
  mapMarketoResponseToState,
  filterByVisibilityRules,
} from "./utils";

const LoaderWrapper = styled.div(({ theme }) => ({
  ...margin(theme.spacing.m100, "auto"),
  width: 200,
}));

export const MarketoForm: React.FC<MarketoFormProps> = ({
  submitButtonLabel = "Submit",
  programName,
  source,
  reason,
  formId,
  successMessage,
  bypassSuccessContentUrl,
  FormHeaderComponent,
  showBackground,
  backgroundColor,
  FormFooterComponent,
  checkWorkEmail,
  hideNonEmailFields,
  isInlineEmail,
  useArrowButtonOnMobile,
  showSuccessMessageBackgroundColor,
  successCallback,
  formLoadedCallback,
  anchor,
  topSpacing: initialTopSpacing,
  horizontalSpacing: initialHorizontalSpacing,
  isChilipiperEnabled,
  isQualifiedEnabled,
  qualifiedExperienceId,
  theme,
  hiddenFields,
  hideSuccessMessageImage,
}) => {
  const { href, search } = useLocation();
  const { navigators } = useSiteContext();
  const router = useRouter();
  const { identify, trackEvent } = useAnalytics();
  const [fieldState, setFieldState] = React.useState<FieldState>({});
  const [requiredFields, setRequiredFields] = React.useState<string[]>([]);
  const [formStatus, setFormStatus] = React.useState<FormStatus>(
    FormStatus.FORM_LOADING,
  );
  const { submit, submitError, setFormError } = useSubmit(setFormStatus);
  const pathname = usePathname();
  const {
    shouldBypass,
    link: bypassLink,
    isInternalLink,
  } = bypassSuccess(bypassSuccessContentUrl);
  const hasInitialized = React.useRef(false);

  const shouldUseQualified =
    checkIfQualifiedEnabled(pathname) && isQualifiedEnabled;

  const topSpacingResult = spacingValuesDecoder.decode(initialTopSpacing);
  const topSpacing = topSpacingResult.ok ? topSpacingResult.value : "none";

  const horizontalSpacingResult = spacingValuesDecoder.decode(
    initialHorizontalSpacing,
  );
  const horizontalSpacing = horizontalSpacingResult.ok
    ? horizontalSpacingResult.value
    : undefined;

  /**
   * Function that handles rerouting, contains some logic based on type of url (external / internal)
   * Moved out to a function for reusability
   */
  const rerouteIfBypass = useCallback(() => {
    isInternalLink
      ? navigators.function(bypassLink)
      : (window.location.href = bypassLink);
  }, [bypassLink, isInternalLink, navigators]);

  const initFormFieldData = async () => {
    if (hasInitialized.current) {
      // Init has already been called, skipping
      return;
    }
    hasInitialized.current = true;

    try {
      const formResponse = await getForm(formId);
      if (!formResponse.success) {
        setFormStatus(FormStatus.FORM_LOAD_FAILED);
        setFormError(genericErrorMessage);
        logGetFormError(formResponse);
        return;
      }

      const mappedMarketoResponse = mapMarketoResponseToState(
        formResponse.result,
        hiddenFields ? hiddenFields : undefined,
      );

      if (formLoadedCallback) {
        formLoadedCallback();
      }

      // Merge with existing field state to preserve any prefilled values
      setFieldState((currentFieldState) => {
        const mergedState = { ...mappedMarketoResponse };

        // When hideNonEmailFields is true, mark non-email fields as not required
        if (hideNonEmailFields) {
          Object.keys(mergedState).forEach((fieldName) => {
            const field = mergedState[fieldName];
            if (field.dataType !== DataType.EMAIL) {
              mergedState[fieldName] = {
                ...field,
                required: false,
              };
            }
          });
        }

        // Preserve existing values if they exist
        Object.keys(currentFieldState).forEach((fieldName) => {
          if (mergedState[fieldName] && currentFieldState[fieldName]?.value) {
            const currentValue = currentFieldState[fieldName].value;
            const targetField = mergedState[fieldName];

            // Ensure value type compatibility
            if (targetField.dataType === DataType.CHECKBOXES) {
              mergedState[fieldName] = {
                ...targetField,
                value: Array.isArray(currentValue)
                  ? currentValue
                  : [currentValue],
              };
            } else {
              mergedState[fieldName] = {
                ...targetField,
                value: Array.isArray(currentValue)
                  ? currentValue[0] || ""
                  : currentValue,
              };
            }
          }
        });

        return mergedState;
      });

      setRequiredFields(
        Object.keys(mappedMarketoResponse).filter((field) => {
          const fieldData = mappedMarketoResponse[field];
          if (hideNonEmailFields) {
            return fieldData.required && fieldData.dataType === DataType.EMAIL;
          }
          return fieldData.required;
        }),
      );
      setFormStatus(FormStatus.FORM_LOAD_SUCCESS);
    } catch (error) {
      setFormStatus(FormStatus.FORM_LOAD_FAILED);
      setFormError(genericErrorMessage);
    }
  };

  React.useEffect(() => {
    initFormFieldData();
    // Disabling this because initFormFieldData will always cause this effect
    // to re-run on re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isLoading: leadIsLoading, prefillData } = usePrefillFromLead(
    href.endsWith("/book-a-demo"),
    search,
  );

  // Handle prefill when data is available and form is loaded
  const hasPrefilled = React.useRef(false);
  React.useEffect(() => {
    if (
      prefillData &&
      formStatus === FormStatus.FORM_LOAD_SUCCESS &&
      fieldState &&
      Object.keys(fieldState).length > 0 &&
      !hasPrefilled.current
    ) {
      // Check if user has already entered data in any of the prefill fields
      const hasUserInput = Object.keys(prefillData).some((fieldName) => {
        const field = fieldState[fieldName];
        if (!field || !field.value) return false;

        // Handle both string and string array values
        if (Array.isArray(field.value)) {
          return (
            field.value.length > 0 && field.value.some((v) => v.trim() !== "")
          );
        }
        return field.value.trim() !== "";
      });

      // Only prefill if user hasn't already entered data
      if (!hasUserInput) {
        setFieldState((currentFieldState) => {
          if (!currentFieldState || typeof currentFieldState !== "object") {
            return currentFieldState;
          }

          const updatedFieldState = { ...currentFieldState };

          // When hideNonEmailFields is true, ensure non-email fields are marked as not required
          if (hideNonEmailFields) {
            Object.keys(updatedFieldState).forEach((fieldName) => {
              const field = updatedFieldState[fieldName];
              if (field.dataType !== DataType.EMAIL) {
                updatedFieldState[fieldName] = {
                  ...field,
                  required: false,
                };
              }
            });
          }

          Object.entries(prefillData).forEach(([fieldName, value]) => {
            if (updatedFieldState[fieldName] && value) {
              const field = updatedFieldState[fieldName];

              try {
                if (field.dataType === DataType.CHECKBOXES) {
                  const checkboxValue = Array.isArray(value)
                    ? value.map(String)
                    : [String(value)];
                  updatedFieldState[fieldName] = {
                    ...field,
                    value: checkboxValue,
                  };
                } else {
                  // For all other field types, ensure value is a string
                  const stringValue = Array.isArray(value)
                    ? value[0] || ""
                    : String(value);
                  updatedFieldState[fieldName] = {
                    ...field,
                    value: stringValue,
                  };
                }
              } catch (fieldError) {
                console.error(`Error updating field ${fieldName}:`, fieldError);
              }
            }
          });

          return updatedFieldState;
        });
      }

      hasPrefilled.current = true;
    }
  }, [prefillData, formStatus, fieldState, hideNonEmailFields]);

  const successCallbackWrapper = React.useCallback(
    (formValues?: FormValues) => {
      trackEvent(
        new MarketingFormSubmittedEvent(programName, {
          ...formValues,
          formId,
        }),
      );
      setFormStatus(FormStatus.SUCCESS);
      if (successCallback) {
        successCallback(formValues);
      }
    },
    [formId, programName, successCallback, trackEvent],
  );

  const onSubmit = React.useCallback(
    async (event) => {
      // reset fields required property before resubmitting
      const fieldStateWithRequiredResets = { ...fieldState };

      // When hideNonEmailFields is true, only reset email fields as required
      if (hideNonEmailFields) {
        Object.keys(fieldStateWithRequiredResets).forEach((fieldName) => {
          const field = fieldStateWithRequiredResets[fieldName];
          if (field.dataType === DataType.EMAIL) {
            fieldStateWithRequiredResets[fieldName].required = true;
          } else {
            fieldStateWithRequiredResets[fieldName].required = false;
          }
        });
      } else {
        requiredFields.forEach((field) => {
          fieldStateWithRequiredResets[field].required = true;
        });
      }

      setFieldState(fieldStateWithRequiredResets);

      const validateForm = () => {
        let validatedFieldState = { ...fieldState };
        Object.keys(fieldState).forEach((name) => {
          const field = fieldState[name];
          const isVisible = filterByVisibilityRules(field, validatedFieldState);

          const shouldValidateAsRequired = hideNonEmailFields
            ? isVisible && field.dataType === DataType.EMAIL && field.required
            : isVisible && field.required;

          validatedFieldState = {
            ...validatedFieldState,
            [name]: {
              ...field,
              error: validateField({
                fieldValue: field.value,
                isRequired: shouldValidateAsRequired,
                type: field.dataType,
                checkWorkEmail,
              }),
              required: shouldValidateAsRequired,
            },
          };
        });
        setFieldState(validatedFieldState);
        return validatedFieldState;
      };

      setFormStatus(FormStatus.SUBMITTING);
      setFormError(null);

      const validatedFieldState = validateForm();

      const submitResult = submit({ event, fieldState: validatedFieldState });
      const hasErrors = submitResult.hasErrors;
      if (hasErrors) {
        return;
      }
      //split up the form values from the state
      const formValues: FormValues = {};
      Object.keys(fieldState).map(
        (field) => (formValues[field] = fieldState[field].value),
      );

      const leadData: LeadRequestBody = {
        programName,
        source: source ?? href,
        reason,
        lookupField: "email",
        input: [
          {
            //we have to make sure that the keys are the same as Marketo's
            ...formValues,
          },
        ],
      };
      try {
        //analytics
        identify({ properties: formValues });
        const formResponse = await pushLeads(leadData, formId);
        if (!formResponse.success) {
          setFormError(genericErrorMessage);
          logPushLeadsError(formResponse);
          setFormStatus(FormStatus.FAIL);
          return;
        }

        if (isChilipiperEnabled && window.ChiliPiper) {
          submitLeadRoutingRequest(
            window.ChiliPiper,
            successCallbackWrapper,
            trackEvent,
            { ...formValues },
            {
              router: "marketo-router",
            },
          );
        } else if (
          shouldUseQualified &&
          window.qualified &&
          typeof window.qualified === "function" &&
          qualifiedExperienceId
        ) {
          const qualified = window.qualified;
          const data = {
            email: formValues.Email,
            company: formValues.Company,
            first_name: formValues.FirstName,
            last_name: formValues.LastName,
            us_ein: formValues.hasUSEIN,
            phone_number: formValues.Phone,
            pain_point: formValues.painPoint,
            has_professional_funding: formValues.hasProfessionalFunding,
            monthly_credit_card_spend: formValues.Monthly_credit_card_spend__c,
            interested_brex_solutions: formValues.interestedBrexsolutions,
            in_operation_for_2_years: formValues.inoperationfor2years,
            employees_marketo_picklist: formValues.Employees_Marketo__c,
            average_monthly_revenue: formValues.averageMonthlyRevenue,
            conversion_rep: undefined,
            name: undefined,
            qgad: undefined,
            qgterm: undefined,
            qualified_experience: undefined,
            ref_code: undefined,
            utm_campaign: undefined,
            utm_content: undefined,
            utm_medium: undefined,
            utm_source: undefined,
            utm_term: undefined,
            web_page: undefined,
          };
          qualified("saveFormData", data);
          qualified("emitFormFill", "custom");
          qualified("showFormExperience", qualifiedExperienceId);
          successCallbackWrapper(formValues);
        } else {
          successCallbackWrapper(formValues);
        }
      } catch (error) {
        setFormStatus(FormStatus.FAIL);
        setFormError(genericErrorMessage);
        if (error instanceof Error) {
          logPushLeadsFailed(error);
        }
      }
    },
    [
      checkWorkEmail,
      fieldState,
      requiredFields,
      formId,
      href,
      identify,
      isChilipiperEnabled,
      programName,
      reason,
      setFormError,
      source,
      submit,
      successCallbackWrapper,
      trackEvent,
      qualifiedExperienceId,
      shouldUseQualified,
      hideNonEmailFields,
    ],
  );

  React.useEffect(() => {
    if (bypassLink && shouldBypass && !isInternalLink) {
      router.prefetch(bypassLink);
    }
  }, [isInternalLink, bypassLink, router, shouldBypass]);

  React.useEffect(() => {
    if (formStatus === FormStatus.SUCCESS && shouldBypass && bypassLink) {
      rerouteIfBypass();
    }
  }, [bypassLink, isInternalLink, shouldBypass, rerouteIfBypass, formStatus]);

  const anchorProps = anchor ? { id: anchor } : {};

  const loading =
    formStatus === FormStatus.FORM_LOADING ||
    (shouldBypass && formStatus === FormStatus.SUCCESS) ||
    leadIsLoading;

  if (loading)
    return (
      <LoaderWrapper {...anchorProps}>
        <Loader />
      </LoaderWrapper>
    );
  if (formStatus === FormStatus.FORM_LOAD_FAILED)
    return <ErrorMessage submitError={submitError} anchor={anchor} />;

  if (formStatus === FormStatus.SUCCESS && !shouldBypass) {
    return (
      <SuccessMessage
        theme={theme}
        successMessage={successMessage}
        anchor={anchor}
        hideSuccessMessageImage={hideSuccessMessageImage}
        topSpacing={topSpacing}
        showSuccessMessageBackgroundColor={showSuccessMessageBackgroundColor}
      />
    );
  }

  return (
    <Form
      isInlineEmail={isInlineEmail}
      useArrowButtonOnMobile={useArrowButtonOnMobile}
      showBackground={showBackground}
      backgroundColor={backgroundColor}
      fieldData={fieldState}
      setFields={setFieldState}
      onSubmit={onSubmit}
      submitError={submitError}
      submitStatus={formStatus}
      submitButtonLabel={submitButtonLabel}
      formId={formId}
      anchor={anchor}
      topSpacing={topSpacing}
      horizontalSpacing={horizontalSpacing}
      FormHeaderComponent={FormHeaderComponent}
      FormFooterComponent={FormFooterComponent}
      theme={theme}
      hideNonEmailFields={hideNonEmailFields}
    />
  );
};
